import { useCallback, useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader, Loading, ErrorState } from "@/components/Bits";
import { inputCls, primaryBtnCls, ghostBtnCls } from "@/components/Kit";
import { NoAccess } from "@/components/AppShell";
import { useAuth } from "@/context/AuthContext";

const rup = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const MODES = ["cash", "upi", "card", "insurance", "bank_transfer", "online", "cheque", "other"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

export default function LabBusinessAnalysis({ defaultTab = "monthly" }) {
  const { can } = useAuth();
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [tab, setTab] = useState(defaultTab);
  const [monthly, setMonthly] = useState(null);
  const [cases, setCases] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError(""); setMonthly(null); setCases(null);
    try {
      const [m, c] = await Promise.all([
        api.get("/lab/business/monthly", { params: { month, year } }),
        api.get("/lab/business/case-wise", { params: { from_date: `${year}-${String(month).padStart(2, "0")}-01`, to_date: `${year}-${String(month).padStart(2, "0")}-28` } }),
      ]);
      setMonthly(m.data); setCases(c.data);
    } catch (e) { setError(apiError(e)); }
  }, [month, year]);
  useEffect(() => { load(); }, [load]);

  if (!can("lab.reports.view")) return <NoAccess what="business analysis" />;

  return (
    <div data-testid="lab-business-analysis-page">
      <PageHeader overline="Business" title="Business Analysis" subtitle="Monthly income by payment mode and case-type performance — from real transactions." />
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <select className={inputCls} value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="ba-month">{MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}</select>
        <input type="number" className={`${inputCls} w-28`} value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="ba-year" />
        <button className={primaryBtnCls} onClick={load} data-testid="ba-show">Show</button>
        <button className={ghostBtnCls} onClick={() => window.print()}>Print</button>
      </div>
      <div className="flex gap-2 mb-4">
        {[["monthly", "Monthly overview"], ["casewise", "Case wise"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`ba-tab-${k}`} className={`px-4 py-2 rounded-full text-sm ${tab === k ? "bg-black text-white" : "bg-slate-100 text-slate-600"}`}>{l}</button>)}
      </div>
      {error && <ErrorState message={error} />}
      {tab === "monthly" && (!monthly ? <Loading /> : (
        <>
          <div className="flex gap-3 mb-4 text-sm"><div className="panel px-4 py-2">Income: <b className="text-emerald-700">{rup(monthly.income)}</b></div><div className="panel px-4 py-2">Expenses: <b className="text-red-600">{rup(monthly.expenses)}</b></div><div className="panel px-4 py-2">Net: <b>{rup(monthly.net_income)}</b></div></div>
          <div className="panel overflow-x-auto"><table className="w-full text-sm" data-testid="ba-monthly-table">
            <thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-3 py-3">Day</th>{MODES.map((m) => <th key={m} className="px-3 py-3 capitalize">{m.replace("_", " ")}</th>)}<th className="px-3 py-3">Total</th></tr></thead>
            <tbody>
              {monthly.rows.map((r) => <tr key={r.day} className="border-b border-slate-50"><td className="px-3 py-2 font-mono text-xs">{r.day}</td>{MODES.map((m) => <td key={m} className="px-3 py-2">{r[m] ? rup(r[m]) : "0"}</td>)}<td className="px-3 py-2 font-medium">{rup(r.total)}</td></tr>)}
              <tr className="font-semibold bg-slate-50"><td className="px-3 py-2">Total</td>{MODES.map((m) => <td key={m} className="px-3 py-2">{rup(monthly.totals[m])}</td>)}<td className="px-3 py-2">{rup(monthly.income)}</td></tr>
            </tbody>
          </table></div>
        </>
      ))}
      {tab === "casewise" && (!cases ? <Loading /> : (
        <div className="panel overflow-x-auto"><table className="w-full text-sm" data-testid="ba-casewise-table">
          <thead><tr className="text-left text-xs text-slate-400 border-b"><th className="px-4 py-3">Case type</th><th className="px-4 py-3">Count</th><th className="px-4 py-3">Business</th></tr></thead>
          <tbody>
            {cases.rows.length === 0 ? <tr><td colSpan={3} className="px-4 py-10 text-center text-slate-400">No cases this month.</td></tr> : cases.rows.map((r) => <tr key={r.case_type} className="border-b border-slate-50"><td className="px-4 py-2 capitalize font-medium">{(r.case_type || "").replace("_", " ")}</td><td className="px-4 py-2">{r.count}</td><td className="px-4 py-2">{rup(r.business)}</td></tr>)}
            {cases.rows.length > 0 && <tr className="font-semibold bg-slate-50"><td className="px-4 py-2">Total</td><td className="px-4 py-2" /><td className="px-4 py-2">{rup(cases.total_business)}</td></tr>}
          </tbody>
        </table></div>
      ))}
    </div>
  );
}
